import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import './Home.css';
import Footer from '../components/Footer';
const aboutIllustration = '/assets/main/trust.jpeg';
const devopsArtwork = '/assets/main/devops.jpeg';
const cloudArtwork = '/assets/main/cloud.jpeg';
const dataScienceArtwork = '/assets/main/datascience.jpeg';
const dataEngineeringArtwork = '/assets/main/dataeng.jpeg';
const generativeAIArtwork = '/assets/main/genai.jpeg';
const appDevelopmentArtwork = '/assets/main/app.jpeg';
const hero = '/assets/main/heroo.jpeg';

const Home = () => {
  const [heroRef, heroInView] = useInView({ threshold: 0.3, triggerOnce: true });
  const [servicesRef, servicesInView] = useInView({ threshold: 0.01, triggerOnce: true, rootMargin: '0px 0px -10% 0px' });
  const [aboutRef, aboutInView] = useInView({ threshold: 0.3, triggerOnce: true });

  const services = [
    {
      title: 'DevOps Platform Engineering',
      image: devopsArtwork,
      description: 'Build a resilient delivery engine that keeps releases reliable, compliant, and observable from commit to production.',
      highlights: [
        'Enterprise CI/CD automation and release governance',
        'Infrastructure as code blueprints and policy guardrails',
        'Site reliability engineering with proactive observability'
      ],
      path: '/devops'
    },
    {
      title: 'Cloud Architecture & Operations',
      image: cloudArtwork,
      description: 'Design and manage landing zones that accelerate migration, modernization, and day-two operations across any cloud.',
      highlights: [
        'Migration sprints aligned to critical workloads',
        'Automated landing zones and cost governance',
        '24/7 managed operations and security posture management'
      ],
      path: '/cloud'
    },
    {
      title: 'Data Science & Decision Intelligence',
      image: dataScienceArtwork,
      description: 'Translate data into repeatable decisions with production-grade analytics, ML models, and executive-ready insights.',
      highlights: [
        'Predictive models tuned to business impact',
        'Real-time dashboards and narrative storytelling',
        'Model governance, monitoring, and lifecycle care'
      ],
      path: '/data-science'
    },
    {
      title: 'Data Engineering & Modern Warehousing',
      image: dataEngineeringArtwork,
      description: 'Engineer trusted pipelines that move, transform, and activate data across cloud, hybrid, and real-time ecosystems.',
      highlights: [
        'Streaming and batch pipelines with quality contracts',
        'Lakehouse, warehouse, and mesh reference designs',
        'Data governance, cataloging, and FinOps visibility'
      ],
      path: '/data-engineering'
    },
    {
      title: 'Generative AI Innovation Studio',
      image: generativeAIArtwork,
      description: 'Co-create responsible AI copilots, content systems, and automation assistants tailored to your brand and workflows.',
      highlights: [
        'Use-case discovery and responsible AI guardrails',
        'Custom LLM fine-tuning and retrieval pipelines',
        'Pilot launches, measurement frameworks, and scale-out'
      ],
      path: '/generative-ai'
    },
    {
      title: 'Product & App Development',
      image: appDevelopmentArtwork,
      description: 'Ship customer experiences faster with modern front-end, backend, and mobile engineering backed by UX research.',
      highlights: [
        'Design systems, prototyping, and usability validation',
        'API-first architecture with automated quality gates',
        'Managed releases, support runbooks, and analytics'
      ],
      path: '/app-development'
    }
  ];

  const partnerPillars = [
    {
      title: 'Discover & Architect',
      copy: 'Strategy workshops, current-state diagnostics, and roadmap blueprints ensure we target measurable outcomes from day one.'
    },
    {
      title: 'Engineer & Launch',
      copy: 'Cross-functional squads deliver secure, automated, and testable solutions with embedded governance and documentation.'
    },
    {
      title: 'Operate & Evolve',
      copy: 'Runbooks, monitoring, and continuous optimisation keep platforms high-performing while our experts coach your teams.'
    }
  ];

  const toolbeltRowA = [
    { title: 'Kubernetes', caption: 'Orchestrate everything' },
    { title: 'Terraform', caption: 'Infrastructure as code' },
    { title: 'Argo CD', caption: 'Progressive delivery' },
    { title: 'Databricks', caption: 'Lakehouse platform' },
    { title: 'Snowflake', caption: 'Data cloud' },
    { title: 'LangChain', caption: 'AI orchestration' },
    { title: 'Figma', caption: 'Product design systems' },
    { title: 'Azure DevOps', caption: 'Delivery at scale' }
  ];

  const toolbeltRowB = [
    { title: 'Prometheus', caption: 'Telemetry' },
    { title: 'Grafana', caption: 'Observability' },
    { title: 'Pinecone', caption: 'Vector search' },
    { title: 'Power BI', caption: 'Decision intelligence' },
    { title: 'CircleCI', caption: 'CI velocity' },
    { title: 'Apache Kafka', caption: 'Event backbone' },
    { title: 'Flutter', caption: 'Multi-platform apps' },
    { title: 'Vault', caption: 'Secrets automation' }
  ];

  return (
    <motion.div 
      className="home"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.6 }}
    >
      {/* Company Introduction Section */}
      <section className="hero parallax-container" ref={heroRef}>
        <div className="parallax-bg"></div>
        <div className="container">
          <div className="hero-layout">
            <div className="hero-two-column">
              <motion.div 
                className="hero-content-left"
                initial={{ opacity: 0, x: -30 }}
                animate={heroInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <motion.h1
                  initial={{ opacity: 0, y: 30 }}
                  animate={heroInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.8, delay: 0.4 }}
                >
                  End-to-End Digital Transformation, Delivered
                </motion.h1>
                <motion.p
                  className="hero-description"
                  initial={{ opacity: 0, y: 30 }}
                  animate={heroInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.8, delay: 0.6 }}
                >
                  CloudRaize architects, engineers, and operates modern platforms that compound value. From cloud landing zones and automation to AI-driven customer experiences, we orchestrate every layer required to deliver measurable, enterprise-wide transformation.
                </motion.p>
                <motion.div 
                  className="hero-buttons left"
                  initial={{ opacity: 0, y: 30 }}
                  animate={heroInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.8, delay: 0.8 }}
                >
                  <Link to="/contact" className="btn btn-primary">Get Started</Link>
                </motion.div>
              </motion.div>

              <motion.div 
                className="hero-side-image right"
                initial={{ opacity: 0, x: 40 }}
                animate={heroInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.3 }}
              >
                <img src={hero} alt="Hero visual" />
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="services-section section" ref={servicesRef}>
        <div className="container">
          <motion.div 
            className="section-header text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={servicesInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <h2>Our Expertise</h2>
            <p>Comprehensive IT solutions tailored to your business needs</p>
          </motion.div>
          
          <div className="services-grid">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                className="service-card"
                initial={{ opacity: 0, y: 50 }}
                animate={servicesInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ y: -12 }}
              >
                <div className="service-image">
                  <img src={service.image} alt={service.title} />
                </div>
                <div className="service-content">
                  <div className="service-subtitle">IT Solutions</div>
                  <h3>{service.title}</h3>
                  <p>{service.description}</p>
                  <ul className="service-highlights">
                    {service.highlights.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                  <Link to={service.path} className="service-link">
                    Learn More â†’
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Toolbelt Section */}
      <section className="toolbelt-section section">
        <div className="container">
          <div className="toolbelt-header">
            <span className="toolbelt-kicker">Our Technical Fabric</span>
            <h2>The toolchain we live in every day</h2>
            <p>
              From resilient platforms to intelligent applications, these are the building blocks our teams reach for first. Nested automation keeps delivery calm while innovation stays fast.
            </p>
          </div>

          <div className="tools-marquee">
            <div className="marquee-track">
              {[...toolbeltRowA, ...toolbeltRowA].map((tool, index) => (
                <div className="marquee-card" key={`rowA-${index}-${tool.title}`}>
                  <span className="marquee-card__label">{tool.title}</span>
                  {tool.caption && <span className="marquee-card__caption">{tool.caption}</span>}
                </div>
              ))}
            </div>
          </div>

          <div className="tools-marquee reverse">
            <div className="marquee-track">
              {[...toolbeltRowB, ...toolbeltRowB].map((tool, index) => (
                <div className="marquee-card" key={`rowB-${index}-${tool.title}`}>
                  <span className="marquee-card__label">{tool.title}</span>
                  {tool.caption && <span className="marquee-card__caption">{tool.caption}</span>}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* About Preview Section */}
      <section className="about-preview section section-alt" ref={aboutRef}>
        <div className="container">
          <div className="grid-2">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={aboutInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8 }}
            >
              <div className="about-image">
                <img 
                  src={aboutIllustration} 
                  alt="Illustration of CloudRaize delivery teams working together" 
                  className="about-illustration"
                />
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={aboutInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <h2>Why Clients Trust CloudRaize</h2>
              <p>
                We operate as an extension of your technology organisation, pairing senior architects, delivery leads, and product strategists who stay embedded from initial discovery through run-state optimisation. Every programme is measured, transparent, and supported by enablement.
              </p>
              <div className="stats">
                <div className="stat">
                  <h3>500+</h3>
                  <p>Digital initiatives launched across the globe</p>
                </div>
                <div className="stat">
                  <h3>120+</h3>
                  <p>Enterprise and scale-up partners</p>
                </div>
                <div className="stat">
                  <h3>24/7</h3>
                  <p>Follow-the-sun support and monitoring</p>
                </div>
              </div>
              <div className="pillars-grid">
                {partnerPillars.map((pillar) => (
                  <div className="pillar-card" key={pillar.title}>
                    <h3>{pillar.title}</h3>
                    <p>{pillar.copy}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      
      <Footer />
    </motion.div>
  );
};

export default Home;

